import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { useMutation, useQuery, type QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "@shared/services/apiClient";
import apiClient from "@shared/services/apiClient";
import {
  FALLBACK_DISPLAY_PROFILE_POLICY,
  INLINE_PLAYBACK_SPEED_OPTIONS,
  type DisplayProfileConfig,
  type DisplayProfilePolicyResponse,
  type DisplayProfilesResponse,
  type EEGDisplayProfile,
} from "../types/eegViewerDisplayProfileTypes";

function toUserFacingApiError(error: unknown): {
  status: number | null;
  message: string;
} {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  const status = axiosError?.response?.status ?? null;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    axiosError?.message ||
    "Request failed.";
  return { status, message };
}

export function useEEGDisplayProfile(params: {
  startupSecondaryQueriesEnabled: boolean;
  queryClient: QueryClient;
  currentPlaybackSpeed: number;
  collapsePlaybackSpeedOptions: boolean;
  buildDisplayProfileConfigRef: MutableRefObject<
    () => DisplayProfileConfig | null
  >;
  setCurrentConfig: React.Dispatch<
    React.SetStateAction<{ config_id?: string; playback_speed: number } | null>
  >;
}) {
  const {
    startupSecondaryQueriesEnabled,
    queryClient,
    currentPlaybackSpeed,
    collapsePlaybackSpeedOptions,
    buildDisplayProfileConfigRef,
    setCurrentConfig,
  } = params;

  const [selectedDisplayProfileId, setSelectedDisplayProfileId] = useState("");
  const [newDisplayProfileName, setNewDisplayProfileName] = useState("");
  const [displayProfileStatus, setDisplayProfileStatus] = useState<
    string | null
  >(null);
  const [displayProfileError, setDisplayProfileError] = useState<string | null>(
    null,
  );
  const [displayProfileConflict, setDisplayProfileConflict] = useState<
    string | null
  >(null);

  const {
    data: displayProfilesResponse,
    isFetching: isFetchingDisplayProfiles,
    refetch: refetchDisplayProfiles,
  } = useQuery({
    queryKey: ["eeg-display-profiles"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/display-profiles");
      return response.data as DisplayProfilesResponse;
    },
    enabled: startupSecondaryQueriesEnabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: displayProfilePolicyResponse } = useQuery({
    queryKey: ["eeg-display-profile-policy"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/display-profiles/policy");
      return response.data as DisplayProfilePolicyResponse;
    },
    enabled: startupSecondaryQueriesEnabled,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  const displayProfilePolicy =
    displayProfilePolicyResponse || FALLBACK_DISPLAY_PROFILE_POLICY;

  const displayProfiles = displayProfilesResponse?.profiles || [];
  const selectedDisplayProfile =
    displayProfiles.find(
      (profile) => profile.profile_id === selectedDisplayProfileId,
    ) || null;

  const playbackSpeedOptions = useMemo(() => {
    const fallback = FALLBACK_DISPLAY_PROFILE_POLICY.playback_speed.options;
    const source = Array.isArray(displayProfilePolicy.playback_speed?.options)
      ? displayProfilePolicy.playback_speed.options
      : fallback;
    const normalized = source
      .filter((value) => Number.isFinite(value) && value > 0)
      .map((value) => Number(value));
    const deduped = Array.from(new Set(normalized));
    deduped.sort((a, b) => a - b);
    return deduped.length ? deduped : fallback;
  }, [displayProfilePolicy.playback_speed?.options]);

  const playbackSpeedDefault = useMemo(() => {
    const candidate = Number(displayProfilePolicy.playback_speed?.default);
    if (
      Number.isFinite(candidate) &&
      playbackSpeedOptions.includes(candidate)
    ) {
      return candidate;
    }
    return playbackSpeedOptions[0];
  }, [displayProfilePolicy.playback_speed?.default, playbackSpeedOptions]);

  const playbackSpeedButtonOptions = useMemo(() => {
    const currentSpeed = Number(currentPlaybackSpeed);
    const merged = [...new Set([...playbackSpeedOptions, currentSpeed])].filter(
      (value) => Number.isFinite(value) && value > 0,
    );
    merged.sort((a, b) => a - b);
    return merged;
  }, [currentPlaybackSpeed, playbackSpeedOptions]);

  const primaryPlaybackSpeedOptions = useMemo(() => {
    if (!collapsePlaybackSpeedOptions) {
      return playbackSpeedButtonOptions;
    }
    const currentSpeed = Number(currentPlaybackSpeed);
    const inline = playbackSpeedButtonOptions.filter((speed) =>
      (INLINE_PLAYBACK_SPEED_OPTIONS as readonly number[]).includes(speed),
    );
    const primary = new Set(inline);
    if (Number.isFinite(currentSpeed) && currentSpeed > 0) {
      primary.add(currentSpeed);
    }
    return Array.from(primary).sort((a, b) => a - b);
  }, [
    collapsePlaybackSpeedOptions,
    currentPlaybackSpeed,
    playbackSpeedButtonOptions,
  ]);

  const overflowPlaybackSpeedOptions = useMemo(
    () =>
      playbackSpeedButtonOptions.filter(
        (speed) => !primaryPlaybackSpeedOptions.includes(speed),
      ),
    [playbackSpeedButtonOptions, primaryPlaybackSpeedOptions],
  );

  useEffect(() => {
    if (!displayProfiles.length) {
      if (selectedDisplayProfileId) {
        setSelectedDisplayProfileId("");
      }
      return;
    }

    if (
      selectedDisplayProfileId &&
      displayProfiles.some(
        (profile) => profile.profile_id === selectedDisplayProfileId,
      )
    ) {
      return;
    }

    const nextDefault =
      displayProfilesResponse?.effective_default_profile_id ||
      displayProfiles.find((profile) => profile.is_default)?.profile_id ||
      displayProfiles[0].profile_id;
    setSelectedDisplayProfileId(nextDefault);
  }, [
    displayProfiles,
    displayProfilesResponse?.effective_default_profile_id,
    selectedDisplayProfileId,
  ]);

  useEffect(() => {
    setCurrentConfig((previous) => {
      if (!previous) return previous;

      if (!previous.config_id) {
        if (previous.playback_speed === playbackSpeedDefault) {
          return previous;
        }
        return { ...previous, playback_speed: playbackSpeedDefault };
      }

      if (playbackSpeedOptions.includes(previous.playback_speed)) {
        return previous;
      }

      return { ...previous, playback_speed: playbackSpeedDefault };
    });
  }, [playbackSpeedDefault, playbackSpeedOptions, setCurrentConfig]);

  const createDisplayProfileMutation = useMutation({
    mutationFn: async () => {
      const profileName = newDisplayProfileName.trim();
      if (!profileName) {
        throw new Error("Profile name is required.");
      }
      const configPayload = buildDisplayProfileConfigRef.current();
      if (!configPayload) {
        throw new Error("No active EEG configuration available.");
      }
      const response = await apiClient.post("/eeg/display-profiles", {
        profile_name: profileName,
        config_json: configPayload,
      });
      return response.data as EEGDisplayProfile;
    },
    onSuccess: async (profile) => {
      setDisplayProfileError(null);
      setDisplayProfileConflict(null);
      setDisplayProfileStatus(`Created profile "${profile.profile_name}".`);
      setNewDisplayProfileName("");
      setSelectedDisplayProfileId(profile.profile_id);
      await queryClient.invalidateQueries({
        queryKey: ["eeg-display-profiles"],
      });
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setDisplayProfileStatus(null);
      setDisplayProfileConflict(null);
      setDisplayProfileError(normalized.message);
    },
  });

  const updateDisplayProfileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDisplayProfile) {
        throw new Error("Select a profile first.");
      }
      const configPayload = buildDisplayProfileConfigRef.current();
      if (!configPayload) {
        throw new Error("No active EEG configuration available.");
      }
      const response = await apiClient.put(
        `/eeg/display-profiles/${selectedDisplayProfile.profile_id}`,
        {
          profile_name: selectedDisplayProfile.profile_name,
          config_json: configPayload,
          version: selectedDisplayProfile.version,
        },
      );
      return response.data as EEGDisplayProfile;
    },
    onSuccess: async (profile) => {
      setDisplayProfileError(null);
      setDisplayProfileConflict(null);
      setDisplayProfileStatus(`Updated profile "${profile.profile_name}".`);
      await queryClient.invalidateQueries({
        queryKey: ["eeg-display-profiles"],
      });
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setDisplayProfileStatus(null);
      if (normalized.status === 409) {
        setDisplayProfileConflict(
          "Profile was changed elsewhere. Reload profiles and retry.",
        );
        setDisplayProfileError(null);
        return;
      }
      setDisplayProfileConflict(null);
      setDisplayProfileError(normalized.message);
    },
  });

  const setDefaultDisplayProfileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDisplayProfile) {
        throw new Error("Select a profile first.");
      }
      const response = await apiClient.post(
        `/eeg/display-profiles/${selectedDisplayProfile.profile_id}/set-default`,
        {},
      );
      return response.data as EEGDisplayProfile;
    },
    onSuccess: async (profile) => {
      setDisplayProfileError(null);
      setDisplayProfileConflict(null);
      setDisplayProfileStatus(
        `"${profile.profile_name}" set as default profile.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["eeg-display-profiles"],
      });
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setDisplayProfileStatus(null);
      setDisplayProfileConflict(null);
      setDisplayProfileError(normalized.message);
    },
  });

  const deleteDisplayProfileMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDisplayProfile) {
        throw new Error("Select a profile first.");
      }
      await apiClient.delete(
        `/eeg/display-profiles/${selectedDisplayProfile.profile_id}`,
        {
          data: {},
        },
      );
    },
    onSuccess: async () => {
      const deletedName = selectedDisplayProfile?.profile_name || "Profile";
      setDisplayProfileError(null);
      setDisplayProfileConflict(null);
      setDisplayProfileStatus(`Deleted "${deletedName}".`);
      setSelectedDisplayProfileId("");
      await queryClient.invalidateQueries({
        queryKey: ["eeg-display-profiles"],
      });
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setDisplayProfileStatus(null);
      setDisplayProfileConflict(null);
      setDisplayProfileError(normalized.message);
    },
  });

  return {
    selectedDisplayProfileId,
    setSelectedDisplayProfileId,
    newDisplayProfileName,
    setNewDisplayProfileName,
    displayProfileStatus,
    setDisplayProfileStatus,
    displayProfileError,
    setDisplayProfileError,
    displayProfileConflict,
    setDisplayProfileConflict,
    displayProfilesResponse,
    isFetchingDisplayProfiles,
    refetchDisplayProfiles,
    displayProfilePolicy,
    displayProfiles,
    selectedDisplayProfile,
    playbackSpeedOptions,
    playbackSpeedDefault,
    playbackSpeedButtonOptions,
    primaryPlaybackSpeedOptions,
    overflowPlaybackSpeedOptions,
    createDisplayProfileMutation,
    updateDisplayProfileMutation,
    setDefaultDisplayProfileMutation,
    deleteDisplayProfileMutation,
  };
}
